import { lazy, useContext, useState } from 'react'
import { Loader, NVButton } from '@nv/basic-components'
import { TextField } from '@nv/basic-components'
import { useNVModal } from '@nv/components'
import { LoginFlowContext } from '@nv/contexts'
import { AdminVoterAuthService, VoterAuthService } from '@nv/services'
import { nxConfig } from '@nv/utils'
import { useFormik } from 'formik'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import { EmailSent } from './email_sent/EmailSent'
import styles from './ResetPassword.module.scss'
import '@djthoms/pretty-checkbox'
const NVBasicModal = lazy(() => import('@nv/lazy-loadable/lib/modals/NVBasicModal'))

const initialValues = {
  username: '',
  email: '',
}

export function ResetPassword() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname
  const [loading, setLoading] = useState(false)
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [isPWSent, setIsPWSent] = useState(false)
  const [isAdminRequest, setIsAdminRequest] = useState(false)
  const LOGIN_CONTEXT = useContext(LoginFlowContext)
  const { NVModal: PwResetErrorModal, openModal: openPwResetErrorModal } = useNVModal()

  const loginSchema = Yup.object().shape({
    username: Yup.string().required(
      pathname.includes('admin') ? t('auth.login.validations.username.admin') : t('auth.login.validations.username.voter'),
    ),
    email: Yup.string().required(t('admin.users.emailRequired')),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: (values) => resetPassword(values),
  })

  const resetPassword = async (values) => {
    setLoading(true)

    try {
      if (!executeRecaptcha) return

      if (pathname.includes('admin')) {
        const token = await executeRecaptcha('admin_passwordreset')
        // if admins can reset their own passwords
        if (nxConfig.adminAuthSelfManageEnabled) {
          const result = await AdminVoterAuthService.passwordReset(values.username, values.email, token)
          if (result.success) {
            setLoading(false)
            setIsPWSent(true)
          }
        } else {
          const result = await AdminVoterAuthService.requestPasswordReset(values.username, values.email, token)
          if (result.success) {
            setLoading(false)
            setIsPWSent(true)
            setIsAdminRequest(true)
          }
        }
      } else {
        const token = await executeRecaptcha('voter_passwordreset')
        const result = await VoterAuthService.requestPasswordReset(values.username, values.email, token)
        if (result.success) {
          setLoading(false)
          setIsPWSent(true)
        }
      }
    } catch (error) {
      openPwResetErrorModal()
    }
  }

  const ForgotPassword = (
    <>
      <PwResetErrorModal>
        <NVBasicModal title={t('admin.users.actions.resetPassword')} text={t('form.messages.resetPwError')} error={true} />
      </PwResetErrorModal>

      <div className={styles['container']}>
        <form
          className={styles['form']}
          onSubmit={(e) => {
            e.preventDefault()
            formik.submitForm()
          }}
        >
          <span className={styles['form__title']}>{t('auth.login.forgotPassword')}</span>

          <div className={styles['form__section']}>
            <TextField
              formik={formik}
              type='text'
              field='username'
              placeholder={pathname.includes('admin') ? t('form.labels.username') : t('auth.login.labels.username.voter')}
              label={pathname.includes('admin') ? t('form.labels.username') : t('auth.login.labels.username.voter')}
              isRequired={true}
            />
          </div>

          <TextField
            formik={formik}
            type='email'
            field='email'
            placeholder={t('form.labels.emailAddress')}
            label={t('form.labels.emailAddress')}
            isRequired={true}
          />

          <div className={styles['form__footer']}>
            {!loading ? (
              <NVButton
                variant='action'
                text={t('admin.users.actions.resetPassword')}
                height={50}
                onClick={() => {
                  formik.submitForm()
                  if (pathname.includes('admin')) LOGIN_CONTEXT.setLoginFlow('Admin Forgot Password')
                  else LOGIN_CONTEXT.setLoginFlow('Forgot Password')
                }}
              />
            ) : (
              <Loader />
            )}
          </div>
        </form>

        <div className={styles['cancel']}>
          <div className={styles['cancel__label']}>
            {t('admin.users.actions.cancelAndGo')}
            <NVButton
              variant='text'
              text={t('main.loginPage')}
              onClick={() => {
                if (pathname.includes('admin')) navigate('/admin/login')
                else navigate('/voting/voter-login/')
              }}
            />
          </div>
        </div>
      </div>
    </>
  )

  return !isPWSent ? ForgotPassword : <EmailSent formik={formik} isAdminRequest={isAdminRequest} />
}
